import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { observer } from 'mobx-react-lite'
import {
  Avatar, Box, Button, Chip, CircularProgress, Divider, IconButton,
  Paper, Stack, Tooltip, Typography,
} from '@mui/material'
import {
  ArrowBack as BackIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Pause as HoldIcon,
  PersonOutline as PersonIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  ShoppingCart as OrderIcon,
  Payments as PaymentIcon,
  AccessTime as TimeIcon,
  CheckCircleOutline as CheckedIcon,
  RadioButtonUnchecked as UncheckedIcon,
  Receipt as InvoiceIcon,
  CalendarToday as CalIcon,
  LocalOffer as TagIcon,
  CallOutlined as CallIcon,
  CallMade as OutgoingIcon,
  CallReceived as IncomingIcon,
  PhoneMissed as MissedIcon,
  HeadsetMic as ConnectedIcon,
} from '@mui/icons-material'
import { toast } from 'react-toastify'
import { orderVerificationApi } from '../lib/api/orderVerificationApi'
import { callStore } from '../lib/stores/callStore'
import type { OrderVerificationStatus, OrderNote, OrderItem } from '../types/verificationTypes'
import { themeColors } from '../app/layout/theme'
import { SideCard } from '../components/shared/SideCard'
import { InfoRow, SummaryRow } from '../components/shared/DetailViewWrappers'
import { EntityDetailTabs } from '../components/shared/EntityDetailTabs'
import { CommentItem, CommentInput, CommentsEmptyState } from '../components/shared/NotesSection'
import { maskPhoneNumber } from '../utils/phoneUtils'

// ── Helpers ───────────────────────────────────────────────────────────────────

function initials(name: string) {
  return name.split(' ').slice(0, 2).map(n => n[0]?.toUpperCase() ?? '').join('')
}
const AVATAR_COLORS = ['#4f46e5', '#0284c7', '#7b1fa2', '#0891b2', '#b45309', '#15803d', '#c62828']
function avatarColor(name: string) {
  let h = 0; for (const c of name) h = c.charCodeAt(0) + ((h << 5) - h)
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length]
}
function fmt(iso?: string | null) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
}
function fmtTime(iso: string) {
  return new Date(iso).toLocaleString('en-IN', {
    day: 'numeric', month: 'short', year: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: true,
  })
}
function fmtDuration(secs?: number | null) {
  if (!secs) return '—'
  const m = Math.floor(secs / 60), s = secs % 60
  return m > 0 ? `${m}m ${s}s` : `${s}s`
}
function fmtCurrency(n?: number | null) {
  if (n == null) return '—'
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n)
}

// ── Status badges ─────────────────────────────────────────────────────────────

const PAYMENT_CFG: Record<string, { bg: string; color: string; border: string }> = {
  'Pending':  { bg: '#FFF8E1', color: '#F57F17', border: '#FFD54F' },
  'Partial':  { bg: '#E3F2FD', color: '#1565C0', border: '#90CAF9' },
  'Paid':     { bg: '#E8F5E9', color: '#2E7D32', border: '#A5D6A7' },
  'Refunded': { bg: '#F3E5F5', color: '#7B1FA2', border: '#CE93D8' },
}
const VERIFY_CFG: Record<string, { bg: string; color: string; border: string }> = {
  'Pending':      { bg: '#FFF8E1', color: '#F57F17', border: '#FFD54F' },
  'Under Review': { bg: '#E3F2FD', color: '#1565C0', border: '#90CAF9' },
  'Approved':     { bg: '#E8F5E9', color: '#2E7D32', border: '#A5D6A7' },
  'Rejected':     { bg: '#FFEBEE', color: '#C62828', border: '#EF9A9A' },
  'On Hold':      { bg: '#FFF3E0', color: '#E65100', border: '#FFCC80' },
}
const ITEM_STATUS_CFG: Record<string, { bg: string; color: string }> = {
  'Active':    { bg: '#E8F5E9', color: '#2E7D32' },
  'Pending':   { bg: '#FFF8E1', color: '#F57F17' },
  'Cancelled': { bg: '#FFEBEE', color: '#C62828' },
}

function Badge({ label, cfg }: { label: string; cfg: { bg: string; color: string; border?: string } }) {
  return (
    <Chip label={label} size="small" sx={{
      height: 22, borderRadius: '6px', bgcolor: cfg.bg, color: cfg.color,
      border: `1px solid ${cfg.border ?? cfg.color + '44'}`,
      fontSize: '11px', fontWeight: 700, '& .MuiChip-label': { px: '9px' },
    }} />
  )
}

// ── Timeline config ───────────────────────────────────────────────────────────
const TL_CFG: Record<string, { color: string; bg: string }> = {
  created:      { color: '#4B3082', bg: '#EDE7FF' },
  payment:      { color: '#2E7D32', bg: '#E8F5E9' },
  verification: { color: '#F57F17', bg: '#FFF8E1' },
  approved:     { color: '#1565C0', bg: '#E3F2FD' },
  invoice:      { color: '#00838F', bg: '#E0F7FA' },
  note:         { color: '#6D4C41', bg: '#EFEBE9' },
}

// ── Tab: Overview ─────────────────────────────────────────────────────────────
function OverviewTab({ data }: { data: NonNullable<Awaited<ReturnType<typeof orderVerificationApi.getById>>> }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Paper elevation={0} sx={{ border: `1px solid ${themeColors.border}`, borderRadius: '12px', overflow: 'hidden' }}>
        <Box sx={{ px: 2.5, py: 1.5, borderBottom: `1px solid ${themeColors.border}`, bgcolor: '#FAFAFA', display: 'flex', alignItems: 'center', gap: 1 }}>
          <OrderIcon sx={{ fontSize: 15, color: themeColors.primary }} />
          <Typography sx={{ fontSize: '13px', fontWeight: 700 }}>Order Information</Typography>
        </Box>
        <Box sx={{ p: 2.5 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0 }}>
            <InfoRow icon={<OrderIcon sx={{ fontSize: 14 }} />} label="Order ID" value={<Typography fontSize="12.5px" fontWeight={600}>{data.orderId}</Typography>} />
            <InfoRow icon={<TagIcon sx={{ fontSize: 14 }} />} label="Product / Service" value={<Typography fontSize="12.5px">{data.productService}</Typography>} />
            <InfoRow icon={<TagIcon sx={{ fontSize: 14 }} />} label="Plan" value={<Typography fontSize="12.5px">{data.plan ?? '—'}</Typography>} />
            <InfoRow icon={<CalIcon sx={{ fontSize: 14 }} />} label="Billing Cycle" value={<Typography fontSize="12.5px">{data.billingCycle ?? '—'}</Typography>} />
            <InfoRow icon={<CalIcon sx={{ fontSize: 14 }} />} label="Order Date" value={<Typography fontSize="12.5px">{fmt(data.orderDate)}</Typography>} />
            <InfoRow icon={<PersonIcon sx={{ fontSize: 14 }} />} label="Quantity" value={<Typography fontSize="12.5px">{data.quantity ?? '—'}</Typography>} />
          </Box>
          <Divider sx={{ my: 1.5 }} />
          <SummaryRow label="Order Value" value={fmtCurrency(data.orderValue)} />
          <SummaryRow label="Discount" value={<Typography fontSize="12.5px" fontWeight={700} color="#c62828">- {fmtCurrency(data.discount)}</Typography>} />
          <Divider sx={{ my: 0.75 }} />
          <SummaryRow label="Final Amount" value={<Typography fontSize="13px" fontWeight={800} color={themeColors.primary}>{fmtCurrency(data.finalAmount)}</Typography>} />
        </Box>
      </Paper>

      <Paper elevation={0} sx={{ border: `1px solid ${themeColors.border}`, borderRadius: '12px', overflow: 'hidden' }}>
        <Box sx={{ px: 2.5, py: 1.5, borderBottom: `1px solid ${themeColors.border}`, bgcolor: '#FAFAFA', display: 'flex', alignItems: 'center', gap: 1 }}>
          <PaymentIcon sx={{ fontSize: 15, color: themeColors.primary }} />
          <Typography sx={{ fontSize: '13px', fontWeight: 700 }}>Payment Information</Typography>
        </Box>
        <Box sx={{ p: 2.5 }}>
          <InfoRow icon={<PaymentIcon sx={{ fontSize: 14 }} />} label="Payment Method" value={<Typography fontSize="12.5px">{data.paymentMethod ?? '—'}</Typography>} />
          <InfoRow icon={<InvoiceIcon sx={{ fontSize: 14 }} />} label="Transaction ID"
            value={<Typography fontSize="12.5px" fontFamily="monospace" fontWeight={600} color={data.transactionId ? 'text.primary' : '#9ca3af'}>{data.transactionId || 'Not yet'}</Typography>} />
          <InfoRow icon={<InvoiceIcon sx={{ fontSize: 14 }} />} label="Invoice Number"
            value={<Typography fontSize="12.5px" fontFamily="monospace" fontWeight={600} color={data.invoiceNumber ? 'text.primary' : '#9ca3af'}>{data.invoiceNumber || 'Not yet'}</Typography>} />
          <Divider sx={{ my: 1.5 }} />
          <SummaryRow label="Amount Paid" value={<Typography fontSize="12.5px" fontWeight={700} color="#2E7D32">{fmtCurrency(data.amountPaid)}</Typography>} />
          <SummaryRow label="Pending Amount" value={<Typography fontSize="12.5px" fontWeight={700} color={(data.pendingAmount ?? 0) > 0 ? '#C62828' : '#9ca3af'}>{fmtCurrency(data.pendingAmount)}</Typography>} />
        </Box>
      </Paper>
    </Box>
  )
}

// ── Tab: Order Items ──────────────────────────────────────────────────────────
function OrderItemsTab({ items }: { items: OrderItem[] }) {
  const subtotal      = items.reduce((s, i) => s + i.unitPrice * i.quantity, 0)
  const totalDiscount = items.reduce((s, i) => s + i.discount, 0)
  const grandTotal    = items.reduce((s, i) => s + i.total, 0)

  if (items.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <OrderIcon sx={{ fontSize: 40, color: '#E0E0E0', mb: 1 }} />
        <Typography sx={{ fontSize: '13px', color: themeColors.text.muted }}>No order items found</Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {items.map((item, idx) => {
        const cfg = ITEM_STATUS_CFG[item.status] ?? ITEM_STATUS_CFG['Pending']
        return (
          <Paper key={item.id} elevation={0} sx={{
            border: `1px solid ${themeColors.border}`, borderRadius: '12px', overflow: 'hidden',
            transition: 'box-shadow 0.15s', '&:hover': { boxShadow: themeColors.shadow.sm },
          }}>
            <Box sx={{ px: 2.5, py: 1.5, bgcolor: '#FAFAFA', borderBottom: `1px solid ${themeColors.border}`, display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ width: 28, height: 28, borderRadius: '8px', bgcolor: '#EDE7FF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Typography sx={{ fontSize: '11px', fontWeight: 800, color: themeColors.primary }}>#{idx + 1}</Typography>
              </Box>
              <Typography sx={{ fontSize: '13px', fontWeight: 700, color: themeColors.text.primary, flex: 1 }}>{item.productName}</Typography>
              <Chip label={item.status} size="small" sx={{ height: 20, borderRadius: '4px', bgcolor: cfg.bg, color: cfg.color, fontSize: '10px', fontWeight: 700, '& .MuiChip-label': { px: '7px' } }} />
              {item.billingCycle && <Chip label={item.billingCycle} size="small" sx={{ height: 20, borderRadius: '4px', bgcolor: '#EDE7FF', color: themeColors.primary, fontSize: '10px', fontWeight: 600, '& .MuiChip-label': { px: '7px' } }} />}
            </Box>
            <Box sx={{ px: 2.5, py: 1.75, display: 'flex', alignItems: 'flex-start', gap: 3, flexWrap: 'wrap' }}>
              <Box sx={{ flex: 1, minWidth: 160 }}>
                <Typography sx={{ fontSize: '11px', color: themeColors.text.muted, mb: 0.25 }}>Description</Typography>
                <Typography sx={{ fontSize: '12.5px', color: themeColors.text.secondary }}>{item.description || '—'}</Typography>
              </Box>
              <Box sx={{ textAlign: 'right', minWidth: 50 }}>
                <Typography sx={{ fontSize: '11px', color: themeColors.text.muted, mb: 0.25 }}>Qty</Typography>
                <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>{item.quantity}</Typography>
              </Box>
              <Box sx={{ textAlign: 'right', minWidth: 90 }}>
                <Typography sx={{ fontSize: '11px', color: themeColors.text.muted, mb: 0.25 }}>Unit Price</Typography>
                <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>{fmtCurrency(item.unitPrice)}</Typography>
              </Box>
              <Box sx={{ textAlign: 'right', minWidth: 80 }}>
                <Typography sx={{ fontSize: '11px', color: themeColors.text.muted, mb: 0.25 }}>Discount</Typography>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: item.discount > 0 ? '#C62828' : '#9ca3af' }}>
                  {item.discount > 0 ? `- ${fmtCurrency(item.discount)}` : '—'}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'right', minWidth: 90 }}>
                <Typography sx={{ fontSize: '11px', color: themeColors.text.muted, mb: 0.25 }}>Total</Typography>
                <Typography sx={{ fontSize: '14px', fontWeight: 800, color: themeColors.primary }}>{fmtCurrency(item.total)}</Typography>
              </Box>
            </Box>
          </Paper>
        )
      })}
      <Paper elevation={0} sx={{ border: `1px solid ${themeColors.primary}33`, borderRadius: '12px', p: 2, bgcolor: `${themeColors.primary}06` }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 4, flexWrap: 'wrap' }}>
          <Box sx={{ textAlign: 'right' }}>
            <Typography sx={{ fontSize: '11px', color: themeColors.text.muted }}>Subtotal</Typography>
            <Typography sx={{ fontSize: '13px', fontWeight: 700 }}>{fmtCurrency(subtotal)}</Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography sx={{ fontSize: '11px', color: themeColors.text.muted }}>Total Discount</Typography>
            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#C62828' }}>- {fmtCurrency(totalDiscount)}</Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography sx={{ fontSize: '11px', color: themeColors.text.muted, mb: 0.25 }}>Grand Total</Typography>
            <Typography sx={{ fontSize: '16px', fontWeight: 800, color: themeColors.primary }}>{fmtCurrency(grandTotal)}</Typography>
          </Box>
        </Box>
      </Paper>
    </Box>
  )
}

// ── Tab: Call History ─────────────────────────────────────────────────────────
function CallHistoryTab({ calls }: { calls: any[] }) {
  const CALL_TYPE_CFG: Record<string, { icon: React.ReactNode; color: string; bg: string }> = {
    OUTGOING: { icon: <OutgoingIcon sx={{ fontSize: 14 }} />, color: '#1565C0', bg: '#E3F2FD' },
    INCOMING: { icon: <IncomingIcon sx={{ fontSize: 14 }} />, color: '#2E7D32', bg: '#E8F5E9' },
    MISSED:   { icon: <MissedIcon   sx={{ fontSize: 14 }} />, color: '#C62828', bg: '#FFEBEE' },
  }
  const STATUS_CFG: Record<string, { color: string }> = {
    CONNECTED:    { color: '#2E7D32' },
    NOT_ANSWERED: { color: '#C62828' },
    BUSY:         { color: '#F57F17' },
    FAILED:       { color: '#9E9E9E' },
  }

  if (calls.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <CallIcon sx={{ fontSize: 40, color: '#E0E0E0', mb: 1 }} />
        <Typography sx={{ fontSize: '13px', color: themeColors.text.muted }}>No calls logged yet</Typography>
        <Typography sx={{ fontSize: '12px', color: themeColors.text.muted, mt: 0.5 }}>Use the "Call" button in the header to place a call</Typography>
      </Box>
    )
  }

  return (
    <Stack spacing={1.5}>
      {calls.map((call: any) => {
        const typeCfg   = CALL_TYPE_CFG[call.callType] ?? CALL_TYPE_CFG['OUTGOING']
        const statusCfg = STATUS_CFG[call.status]      ?? STATUS_CFG['CONNECTED']
        return (
          <Paper key={call.id} elevation={0} sx={{ border: `1px solid ${themeColors.border}`, borderRadius: '12px', p: 2, display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <Box sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: typeCfg.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: typeCfg.color }}>
              {typeCfg.icon}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: '13px', fontWeight: 700, color: statusCfg.color }}>
                  {call.callType.charAt(0) + call.callType.slice(1).toLowerCase()} Call
                </Typography>
                <Typography sx={{ fontSize: '11px', color: themeColors.text.muted }}>•</Typography>
                <Typography sx={{ fontSize: '12px', color: themeColors.text.secondary }}>
                  {call.status.charAt(0) + call.status.slice(1).toLowerCase().replace(/_/g, ' ')}
                </Typography>
                {call.durationSeconds != null && (
                  <>
                    <Typography sx={{ fontSize: '11px', color: themeColors.text.muted }}>•</Typography>
                    <Typography sx={{ fontSize: '12px', color: themeColors.text.secondary }}>{fmtDuration(call.durationSeconds)}</Typography>
                  </>
                )}
                {call.outcome && (
                  <Chip label={call.outcome} size="small" sx={{ height: 18, borderRadius: '4px', bgcolor: '#EDE7FF', color: themeColors.primary, fontSize: '10px', fontWeight: 600, '& .MuiChip-label': { px: '6px' } }} />
                )}
              </Box>
              {call.notes && (
                <Typography sx={{ fontSize: '12px', color: themeColors.text.secondary, mt: 0.5, fontStyle: 'italic' }}>"{call.notes}"</Typography>
              )}
              <Box sx={{ display: 'flex', gap: 1.5, mt: 0.75, flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: '11px', color: themeColors.text.muted }}>{fmtTime(call.startedAt)}</Typography>
                {call.initiatedBy && (
                  <>
                    <Typography sx={{ fontSize: '11px', color: '#D1D5DB' }}>•</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Avatar sx={{ width: 16, height: 16, fontSize: '8px', fontWeight: 700, bgcolor: avatarColor(call.initiatedBy.name) }}>
                        {initials(call.initiatedBy.name)}
                      </Avatar>
                      <Typography sx={{ fontSize: '11px', color: themeColors.primary, fontWeight: 600 }}>{call.initiatedBy.name}</Typography>
                    </Box>
                  </>
                )}
              </Box>
            </Box>
          </Paper>
        )
      })}
    </Stack>
  )
}

// ── Tab: Checklist ────────────────────────────────────────────────────────────
function ChecklistTab({ items }: { items: { id: string; label: string; checked: boolean }[] }) {
  const done = items.filter(i => i.checked).length
  const pct  = items.length > 0 ? Math.round((done / items.length) * 100) : 0
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ flex: 1, height: 6, bgcolor: '#F0F0F0', borderRadius: 4, overflow: 'hidden' }}>
          <Box sx={{ height: '100%', width: `${pct}%`, bgcolor: pct === 100 ? '#2E7D32' : themeColors.primary, borderRadius: 4, transition: 'width 0.4s ease' }} />
        </Box>
        <Typography sx={{ fontSize: '12px', fontWeight: 700, color: pct === 100 ? '#2E7D32' : themeColors.primary, minWidth: 40 }}>{done}/{items.length}</Typography>
      </Box>
      {items.map(item => (
        <Box key={item.id} sx={{
          display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, borderRadius: '10px',
          bgcolor: item.checked ? '#F1FFF5' : '#FAFAFA',
          border: `1px solid ${item.checked ? '#A5D6A7' : '#E5E7EB'}`,
          transition: 'all 0.15s',
        }}>
          {item.checked
            ? <CheckedIcon sx={{ fontSize: 20, color: '#2E7D32', flexShrink: 0 }} />
            : <UncheckedIcon sx={{ fontSize: 20, color: '#9E9E9E', flexShrink: 0 }} />}
          <Typography sx={{ fontSize: '13px', fontWeight: item.checked ? 600 : 400, color: item.checked ? '#1B5E20' : themeColors.text.secondary }}>
            {item.label}
          </Typography>
          {item.checked && <Chip label="Done" size="small" sx={{ ml: 'auto', height: 18, borderRadius: '4px', bgcolor: '#E8F5E9', color: '#2E7D32', fontSize: '10px', fontWeight: 700, '& .MuiChip-label': { px: '6px' } }} />}
        </Box>
      ))}
    </Box>
  )
}

// ── Tab: Timeline ─────────────────────────────────────────────────────────────
function TimelineTab({ events }: { events: { id: string; event: string; description: string; timestamp: string; user: string; type: string }[] }) {
  if (events.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <TimeIcon sx={{ fontSize: 40, color: '#E0E0E0', mb: 1 }} />
        <Typography sx={{ fontSize: '13px', color: themeColors.text.muted }}>No activity yet</Typography>
      </Box>
    )
  }
  return (
    <Box sx={{ position: 'relative', pl: 0.5 }}>
      {events.map((ev, idx) => {
        const cfg    = TL_CFG[ev.type] ?? { color: '#616161', bg: '#F5F5F5' }
        const isLast = idx === events.length - 1
        return (
          <Box key={ev.id} sx={{ display: 'flex', gap: 2, pb: isLast ? 0 : 3, position: 'relative' }}>
            {!isLast && <Box sx={{ position: 'absolute', left: 15, top: 34, bottom: 0, width: 2, bgcolor: '#F0F0F0' }} />}
            <Box sx={{ width: 32, height: 32, borderRadius: '8px', bgcolor: cfg.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, zIndex: 1 }}>
              <TimeIcon sx={{ fontSize: 15, color: cfg.color }} />
            </Box>
            <Box sx={{ pt: 0.25 }}>
              <Typography sx={{ fontSize: '13px', fontWeight: 700, color: themeColors.text.primary }}>{ev.event}</Typography>
              {ev.description && <Typography sx={{ fontSize: '12.5px', color: themeColors.text.secondary, mt: 0.25 }}>{ev.description}</Typography>}
              <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: '11px', color: themeColors.text.muted }}>{fmtTime(ev.timestamp)}</Typography>
                <Typography sx={{ fontSize: '11px', color: '#D1D5DB' }}>•</Typography>
                <Typography sx={{ fontSize: '11px', color: themeColors.primary, fontWeight: 600 }}>{ev.user}</Typography>
              </Box>
            </Box>
          </Box>
        )
      })}
    </Box>
  )
}

// ── Tab: Notes ────────────────────────────────────────────────────────────────
function NotesTab({ notes, onAdd, isAdding }: { notes: OrderNote[]; onAdd: (c: string) => Promise<void>; isAdding: boolean }) {
  const genericNotes = notes.map(n => ({
    id: n.id, content: n.content, createdAt: n.timestamp, updatedAt: n.timestamp,
    createdBy: { id: n.author, name: n.author, email: '' },
  }))
  return (
    <Box>
      <CommentInput onAdd={onAdd} isAdding={isAdding} placeholder="Add an internal note…" />
      <Box sx={{ mt: 2 }}>
        {genericNotes.length === 0
          ? <CommentsEmptyState message="No internal notes yet. Add the first one above." />
          : genericNotes.map((note, idx) => <CommentItem key={note.id} note={note} isLast={idx === genericNotes.length - 1} />)
        }
      </Box>
    </Box>
  )
}

// ── Main Component ────────────────────────────────────────────────────────────

const OrderVerificationDetails = observer(function OrderVerificationDetails() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState(0)
  const [localNotes, setLocalNotes] = useState<OrderNote[]>([])
  const [localCalls, setLocalCalls] = useState<any[]>([])

  // ── Main detail query ─────────────────────────────────────────────────────
  const { data, isLoading } = useQuery({
    queryKey: ['order-verification-detail', id],
    queryFn: () => orderVerificationApi.getById(id!),
    enabled: !!id,
    staleTime: 30_000,
  })

  // ── Live calls query (separate — refreshed after each call) ───────────────
  const { data: liveCalls = [] } = useQuery({
    queryKey: ['ov-calls', id],
    queryFn: () => orderVerificationApi.getCalls(id!),
    enabled: !!id,
    staleTime: 10_000,
  })

  // ── Auto-log calls completed via the global callStore dialer ──────────────
  const lastCallRef = useRef<any>(null)
  const callPhase   = callStore.phase
  const activeCall  = callStore.activeCall

  useEffect(() => {
    if (callPhase === 'OUTCOME' && activeCall) {
      lastCallRef.current = {
        outcome:         activeCall.outcome,
        durationSeconds: activeCall.durationSeconds,
        notes:           activeCall.notes,
      }
    }
  }, [callPhase, activeCall])

  useEffect(() => {
    if (callPhase === 'IDLE' && lastCallRef.current && id) {
      const c = lastCallRef.current
      lastCallRef.current = null
      orderVerificationApi.logCall(id, {
        callType:        'OUTGOING',
        status:          'CONNECTED',
        outcome:         c.outcome || 'CONNECTED',
        notes:           c.notes,
        durationSeconds: c.durationSeconds ?? 0,
      }).then(logged => {
        if (logged?.id) setLocalCalls(prev => [logged, ...prev])
        queryClient.invalidateQueries({ queryKey: ['ov-calls', id] })
        queryClient.invalidateQueries({ queryKey: ['order-verification-detail', id] })
      })
    }
  }, [callPhase, id])

  // ── Mutations ─────────────────────────────────────────────────────────────
  const allNotes = [...(data?.notes ?? []), ...localNotes]
  const allCalls = liveCalls.length > 0 ? liveCalls : [...localCalls]

  const statusMut = useMutation({
    mutationFn: (status: OrderVerificationStatus) => orderVerificationApi.updateStatus(id!, status),
    onSuccess: (_, status) => {
      toast.success(`Status updated to "${status}"`)
      queryClient.invalidateQueries({ queryKey: ['order-verification-detail', id] })
      queryClient.invalidateQueries({ queryKey: ['order-verifications'] })
    },
    onError: () => toast.error('Failed to update status'),
  })

  const noteMut = useMutation({
    mutationFn: (content: string) => orderVerificationApi.addNote(id!, content),
    onSuccess: note => setLocalNotes(prev => [...prev, note]),
    onError: () => toast.error('Failed to add note'),
  })

  if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}><CircularProgress size={36} /></Box>
  if (!data) return (
    <Box sx={{ textAlign: 'center', py: 8 }}>
      <Typography color="text.secondary">Order not found.</Typography>
      <Button sx={{ mt: 2 }} onClick={() => navigate(-1)} startIcon={<BackIcon />}>Go Back</Button>
    </Box>
  )

  const tabs = [
    { label: 'Overview',                                    content: <OverviewTab data={data} /> },
    { label: `Order Items (${data.orderItems?.length ?? 0})`, content: <OrderItemsTab items={data.orderItems ?? []} /> },
    { label: `Calls (${allCalls.length})`,                  content: <CallHistoryTab calls={allCalls} /> },
    { label: 'Checklist',                                   content: <ChecklistTab items={data.checklist} /> },
    { label: 'Timeline',                                    content: <TimelineTab events={data.timeline} /> },
    { label: `Notes (${allNotes.length})`,                  content: <NotesTab notes={allNotes} onAdd={async c => { noteMut.mutate(c) }} isAdding={noteMut.isPending} /> },
  ]

  const paymentCfg = PAYMENT_CFG[data.paymentStatus] ?? PAYMENT_CFG['Pending']
  const verifyCfg  = VERIFY_CFG[data.verificationStatus] ?? VERIFY_CFG['Pending']

  return (
    <Box sx={{
      height: '100%', overflowY: 'auto', bgcolor: '#f5f6fa',
      '&::-webkit-scrollbar': { width: '5px' },
      '&::-webkit-scrollbar-thumb': { bgcolor: '#d1d5db', borderRadius: '4px' },
    }}>

      {/* ── Breadcrumb ── */}
      <Box sx={{ px: 3, pt: 2, pb: 0, display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <IconButton size="small" onClick={() => navigate('/order-verification')} sx={{ color: '#6b7280', mr: 0.5 }}>
          <BackIcon fontSize="small" />
        </IconButton>
        <Typography fontSize="13px" color="text.secondary" sx={{ cursor: 'pointer', '&:hover': { color: 'text.primary' } }} onClick={() => navigate('/order-verification')}>Order Verification</Typography>
        <Typography fontSize="13px" color="text.secondary" sx={{ mx: 0.25 }}>/</Typography>
        <Typography fontSize="13px" fontWeight={600} color="text.primary">{data.orderId}</Typography>
      </Box>

      {/* ── Hero Card ── */}
      <Box sx={{ px: 3, pt: 1.5, pb: 0 }}>
        <Paper elevation={0} sx={{ border: `2px solid ${themeColors.primary}45`, borderRadius: '14px', p: 2.5, bgcolor: '#fff' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
            {/* Icon */}
            <Box sx={{
              width: 56, height: 56, borderRadius: '14px',
              bgcolor: `${themeColors.primary}12`, flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: `1.5px solid ${themeColors.primary}30`,
              boxShadow: `0 4px 12px ${themeColors.primary}20`,
            }}>
              <OrderIcon sx={{ fontSize: 26, color: themeColors.primary }} />
            </Box>

            {/* Title + badges */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 0.5 }}>
                <Typography sx={{ fontSize: '18px', fontWeight: 800, color: themeColors.text.primary, fontFamily: 'monospace' }}>{data.orderId}</Typography>
                <Badge label={data.verificationStatus} cfg={verifyCfg} />
                <Badge label={data.paymentStatus} cfg={paymentCfg} />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Avatar sx={{ width: 20, height: 20, fontSize: '9px', fontWeight: 700, bgcolor: avatarColor(data.customerName) }}>{initials(data.customerName)}</Avatar>
                  <Typography sx={{ fontSize: '12.5px', color: themeColors.text.secondary, fontWeight: 500 }}>{data.customerName}</Typography>
                </Box>
                <Typography sx={{ fontSize: '12px', color: '#D1D5DB' }}>•</Typography>
                <Typography sx={{ fontSize: '12.5px', color: themeColors.text.muted }}>{data.companyName}</Typography>
                <Typography sx={{ fontSize: '12px', color: '#D1D5DB' }}>•</Typography>
                <Typography sx={{ fontSize: '12.5px', color: themeColors.text.muted }}><b style={{ color: themeColors.text.secondary }}>Assigned:</b> {data.assignedUser}</Typography>
                <Typography sx={{ fontSize: '12px', color: '#D1D5DB' }}>•</Typography>
                <Typography sx={{ fontSize: '12.5px', color: themeColors.text.muted }}>{fmt(data.createdDate)}</Typography>
              </Box>
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
              {/* ── Call Button ── */}
              <Tooltip title={data.mobile ? `Call ${data.mobile}` : 'No mobile number'}>
                <span>
                  <Button
                    size="small" variant="outlined"
                    startIcon={callStore.isStarting ? <CircularProgress size={13} sx={{ color: themeColors.primary }} /> : <PhoneIcon sx={{ fontSize: 14 }} />}
                    disabled={callStore.isStarting || !data.mobile}
                    onClick={() => callStore.showPlaceCall({
                      entityType: 'DEAL',         // reuses the DEAL call flow
                      entityId:   id!,
                      name:       data.customerName,
                      phone:      data.mobile ?? '',
                      companyName: data.companyName,
                    })}
                    sx={{
                      fontSize: '12px', height: 32, textTransform: 'none', borderRadius: '8px',
                      borderColor: themeColors.primary, color: themeColors.primary,
                      '&:hover': { bgcolor: `${themeColors.primary}08` },
                    }}
                  >
                    {callStore.isStarting ? 'Calling…' : 'Call'}
                  </Button>
                </span>
              </Tooltip>

              <Button size="small" variant="contained" startIcon={<ApproveIcon sx={{ fontSize: 14 }} />}
                onClick={() => statusMut.mutate('Approved')} disabled={statusMut.isPending || data.verificationStatus === 'Approved'}
                sx={{ fontSize: '12px', height: 32, textTransform: 'none', borderRadius: '8px', bgcolor: '#2E7D32', '&:hover': { bgcolor: '#1B5E20' }, boxShadow: 'none' }}>
                Approve
              </Button>
              <Button size="small" variant="contained" startIcon={<RejectIcon sx={{ fontSize: 14 }} />}
                onClick={() => statusMut.mutate('Rejected')} disabled={statusMut.isPending || data.verificationStatus === 'Rejected'}
                sx={{ fontSize: '12px', height: 32, textTransform: 'none', borderRadius: '8px', bgcolor: '#C62828', '&:hover': { bgcolor: '#B71C1C' }, boxShadow: 'none' }}>
                Reject
              </Button>
              <Button size="small" variant="outlined" startIcon={<HoldIcon sx={{ fontSize: 14 }} />}
                onClick={() => statusMut.mutate('On Hold')} disabled={statusMut.isPending || data.verificationStatus === 'On Hold'}
                sx={{ fontSize: '12px', height: 32, textTransform: 'none', borderRadius: '8px', borderColor: '#E65100', color: '#E65100', '&:hover': { borderColor: '#BF360C', bgcolor: '#FFF3E0' } }}>
                Hold
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>

      {/* ── Two-column body ── */}
      <Box sx={{ display: 'flex', gap: 2.5, p: 3, pt: 2, alignItems: 'flex-start' }}>

        {/* ── Left Sidebar ── */}
        <Box sx={{ width: 260, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>

          <SideCard title="Customer">
            <Stack spacing={0.5}>
              <InfoRow icon={<PersonIcon sx={{ fontSize: 14 }} />} label="Name" value={<Typography fontSize="12.5px" fontWeight={600}>{data.customerName}</Typography>} />
              <InfoRow icon={<BusinessIcon sx={{ fontSize: 14 }} />} label="Company" value={<Typography fontSize="12.5px">{data.companyName}</Typography>} />
              <InfoRow icon={<EmailIcon sx={{ fontSize: 14 }} />} label="Email" value={<Typography fontSize="12.5px" sx={{ wordBreak: 'break-all' }}>{data.email ?? '—'}</Typography>} />
              <InfoRow
                icon={<PhoneIcon sx={{ fontSize: 14 }} />}
                label="Mobile"
                value={
                  data.mobile
                    ? <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Typography fontSize="12.5px">{maskPhoneNumber(data.mobile)}</Typography>
                        <Tooltip title="Place call">
                          <IconButton size="small" disabled={callStore.isStarting}
                            onClick={() => callStore.showPlaceCall({ entityType: 'DEAL', entityId: id!, name: data.customerName, phone: data.mobile!, companyName: data.companyName })}
                            sx={{ width: 22, height: 22, bgcolor: `${themeColors.primary}12`, color: themeColors.primary, '&:hover': { bgcolor: `${themeColors.primary}22` } }}>
                            <PhoneIcon sx={{ fontSize: 12 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    : <Typography fontSize="12.5px" color="#9ca3af">—</Typography>
                }
              />
            </Stack>
          </SideCard>

          <SideCard title="Financial Summary">
            <SummaryRow label="Order Value" value={fmtCurrency(data.orderValue)} />
            <SummaryRow label="Discount" value={<Typography fontSize="12.5px" fontWeight={700} color="#c62828">- {fmtCurrency(data.discount)}</Typography>} />
            <SummaryRow label="Final Amount" value={<Typography fontSize="13px" fontWeight={800} color={themeColors.primary}>{fmtCurrency(data.finalAmount)}</Typography>} />
            <Divider sx={{ my: 1 }} />
            <SummaryRow label="Amount Paid" value={<Typography fontSize="12.5px" fontWeight={700} color="#2E7D32">{fmtCurrency(data.amountPaid)}</Typography>} />
            <SummaryRow label="Pending" value={<Typography fontSize="12.5px" fontWeight={700} color={(data.pendingAmount ?? 0) > 0 ? '#C62828' : '#9ca3af'}>{fmtCurrency(data.pendingAmount)}</Typography>} />
          </SideCard>

          <SideCard title="Assigned To">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.5 }}>
              <Avatar sx={{ width: 34, height: 34, fontSize: '12px', fontWeight: 700, bgcolor: avatarColor(data.assignedUser) }}>{initials(data.assignedUser)}</Avatar>
              <Box>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: themeColors.text.primary }}>{data.assignedUser}</Typography>
                <Typography sx={{ fontSize: '11px', color: themeColors.text.muted }}>Verifier</Typography>
              </Box>
            </Box>
          </SideCard>

          <SideCard title="Dates">
            <InfoRow icon={<CalIcon sx={{ fontSize: 14 }} />} label="Created" value={<Typography fontSize="12.5px">{fmt(data.createdDate)}</Typography>} />
            <InfoRow icon={<CalIcon sx={{ fontSize: 14 }} />} label="Order Date" value={<Typography fontSize="12.5px">{fmt(data.orderDate)}</Typography>} />
          </SideCard>

        </Box>

        {/* ── Right Panel (Tabs) ── */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <EntityDetailTabs
            tabs={tabs}
            activeTab={activeTab}
            onChange={setActiveTab}
            padding={2.5}
            tabHeight={44}
            tabFontSize="13px"
            tabFontWeight={600}
            tabPaddingX={2}
            borderRadius="12px"
          />
        </Box>

      </Box>
    </Box>
  )
})

export default OrderVerificationDetails
